import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getOwnedLibrary } from '../services/api';
import type { OwnedBooksIndex, OwnedLibraryResponse } from '../utils/ownedBooks';
import { EMPTY_OWNED_INDEX, buildOwnedIndex } from '../utils/ownedBooks';

// The Grimmory snapshot is cached server-side, so polling this often is cheap and
// keeps a long-lived tab in sync with books imported elsewhere.
const OWNED_REFRESH_INTERVAL_MS = 5 * 60 * 1000;

interface UseOwnedBooksOptions {
  enabled: boolean;
}

interface UseOwnedBooksReturn {
  ownedIndex: OwnedBooksIndex;
  refreshOwned: () => Promise<void>;
}

export function useOwnedBooks({ enabled }: UseOwnedBooksOptions): UseOwnedBooksReturn {
  const [response, setResponse] = useState<OwnedLibraryResponse | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const refreshOwned = useCallback(async () => {
    if (!enabled) {
      setResponse(null);
      return;
    }

    try {
      const owned = await getOwnedLibrary();
      if (isMountedRef.current) {
        setResponse(owned);
      }
    } catch (error) {
      // Ownership marking is an enhancement; failing to load it must not break search.
      console.warn('Failed to load owned book index:', error);
    }
  }, [enabled]);

  useEffect(() => {
    void refreshOwned();
    if (!enabled) return;

    const intervalId = setInterval(() => {
      void refreshOwned();
    }, OWNED_REFRESH_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [enabled, refreshOwned]);

  const ownedIndex = useMemo(
    () => (enabled ? buildOwnedIndex(response) : EMPTY_OWNED_INDEX),
    [enabled, response],
  );

  return { ownedIndex, refreshOwned };
}
